import { Button, Card, EmptyState, Skeleton } from '@recipes/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { ChefHat, Play, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageHeader } from '@/components/layout/page-header';
import { RecipeCard } from '@/components/recipes/recipe-card';
import { TimerPanel, TimerCueWatcher } from '@/features/cooking/timer-panel';
import { useRecipe, useRecipeList } from '@/features/recipes/queries';
import { RecipeSearchBox } from '@/features/recipes/recipe-search-box';
import { offlineDb, type CookingSession } from '@/lib/offline/db';
import { useState } from 'react';
import { currentFormatLocale } from '@/lib/i18n';

function SessionRow({ session }: { session: CookingSession }) {
  const { t } = useTranslation();
  const recipe = useRecipe(session.recipeId);
  const title = recipe.data?.recipe.title ?? t('cooking.resume.unknownRecipe');
  return (
    <li className="flex items-center gap-3 p-3" data-testid="resume-session">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">
          {t('cooking.resume.progress', { n: session.currentStep + 1 })} ·{' '}
          {new Date(session.updatedAt).toLocaleString(currentFormatLocale())}
        </p>
      </div>
      <Button asChild size="sm">
        <Link to={`/cook/${session.recipeId}`}>
          <Play aria-hidden /> {t('cooking.resume.continue')}
        </Link>
      </Button>
      <Button
        size="icon"
        variant="ghost"
        aria-label={t('cooking.resume.discard')}
        onClick={() => void offlineDb.cookingSessions.delete(session.recipeId)}
      >
        <Trash2 aria-hidden />
      </Button>
    </li>
  );
}

/** /cook: resume an unfinished session or pick a recipe to cook. */
export function CookIndexPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const sessions = useLiveQuery(
    () => offlineDb.cookingSessions.orderBy('updatedAt').reverse().limit(5).toArray(),
    [],
    undefined,
  );
  const recipes = useRecipeList({
    q: q || undefined,
    sort: q ? 'relevance' : 'lastCooked',
    limit: 12,
  });
  const items = recipes.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6" data-testid="cook-index">
      <TimerCueWatcher />
      <PageHeader title={t('cooking.title')} description={t('cooking.indexDescription')} />

      {sessions && sessions.length > 0 && (
        <section aria-labelledby="resume-heading">
          <h2 id="resume-heading" className="mb-2 text-lg font-semibold">
            {t('cooking.resume.title')}
          </h2>
          <ul className="divide-y rounded-lg border">
            {sessions.map((s) => (
              <SessionRow key={s.recipeId} session={s} />
            ))}
          </ul>
        </section>
      )}

      <Card className="p-4">
        <h2 className="mb-2 text-lg font-semibold">{t('cooking.timers.title')}</h2>
        <TimerPanel />
      </Card>

      <section aria-labelledby="pick-heading" className="space-y-3">
        <h2 id="pick-heading" className="text-lg font-semibold">
          {t('cooking.pickRecipe')}
        </h2>
        <RecipeSearchBox value={q} onSearch={setQ} />
        {recipes.isPending ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-56" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon={<ChefHat />} title={t('recipes.library.empty')} />
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((recipe) => (
              <li key={recipe.id}>
                <RecipeCard recipe={recipe} to={`/cook/${recipe.id}`} className="h-full" />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
